import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/empty_state.js';

import { faBuilding, faPlus } from '@fortawesome/free-solid-svg-icons';
import { html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
    type IOrganizationListItem,
    listOrganizations,
} from '../libraries/api_client/organization.js';
import { filterInactive } from '../libraries/display_settings.js';
import { inactiveHiddenHint } from '../libraries/inactive_hint.js';
import { legalFormLabel } from '../libraries/legal_form_options.js';
import {
    type IListResult,
    type IListState,
    ListController,
    listHostStyles,
    listResultFrom,
} from '../libraries/list_controller.js';
import type { IListColumn } from '../libraries/list_table.js';
import { redirectTo } from '../libraries/navigation.js';
import { entityStatusBadge } from '../libraries/status_badge.js';

const organizationColumns: Array<IListColumn<IOrganizationListItem>> = [
    {
        label: 'Name',
        track: 'm',
        cell: organization => organization.name,
    },
    {
        label: 'Rechtsform',
        cell: organization => legalFormLabel(organization.legalForm),
    },
    {
        label: 'Ort',
        track: 'xs',
        cell: organization => organization.city,
    },
    {
        label: 'Status',
        track: 'xs',
        cell: organization => entityStatusBadge(organization.active),
    },
];

@customElement('fm-organization-list')
export class FmOrganizationList extends LitElement {

    static styles = listHostStyles;

    private readonly list = new ListController<IOrganizationListItem>(this, {
        label: 'Organisationen',
        columns: (): Array<IListColumn<IOrganizationListItem>> => organizationColumns,
        rowKey: (organization): string => organization.id,
        rowHref: (organization): string => `/organizations/${organization.id}`,
        withChevron: true,
        rowClass: (organization): string => (organization.active ? '' : 'row-inactive'),
        filterRows: filterInactive,
        errorDescription: 'Die Organisationen konnten nicht geladen werden.',
        load: async (): Promise<IListResult<IOrganizationListItem>> => listResultFrom(
            await listOrganizations(),
            organizations => ({
                rows: organizations,
            })
        ),
        empty: (listState): TemplateResult => this.renderEmptyBody(listState),
        actions: (listState): TemplateResult | typeof nothing => this.renderCreateAction(listState),
    });

    private readonly handleCreate = (): void => {
        redirectTo('/organizations/new');
    };

    private renderCreateButton (slot: string): TemplateResult {
        return html`
            <wa-button slot=${slot} size="s" variant="brand" @click=${this.handleCreate}>
                <fm-icon slot="start" .icon=${faPlus}></fm-icon>
                Organisation anlegen
            </wa-button>
        `;
    }

    /**
     * The heading row's CTA. Left out while the caller has no organisation at
     * all, because the empty-state card carries one itself; a set the display
     * filter emptied still gets it, since that card only links to the settings.
     * @param listState - What the list currently holds.
     * @returns The button, or nothing.
     */
    private renderCreateAction (
        listState: IListState<IOrganizationListItem>
    ): TemplateResult | typeof nothing {
        return listState.unfilteredTotal === 0 ? nothing : this.renderCreateButton('actions');
    }

    private renderEmptyBody (listState: IListState<IOrganizationListItem>): TemplateResult {
        if (listState.unfilteredTotal > 0) {
            return inactiveHiddenHint(
                'Keine aktiven Organisationen',
                'Inaktive Organisationen sind über Ihre Anzeige-Einstellung ausgeblendet. ' +
                'Sie können sie in den Einstellungen wieder einblenden.'
            );
        }

        return html`
            <fm-empty-state
                .icon=${faBuilding}
                heading="Noch keine Organisation angelegt"
                description="Legen Sie eine Organisation an, um Ihre Betriebe zu verwalten.">
                ${this.renderCreateButton('action')}
            </fm-empty-state>
        `;
    }

    render (): TemplateResult {
        return this.list.render(html`
            <fm-breadcrumb slot="breadcrumb" .items=${[
                {
                    label: 'Alle',
                },
            ]}></fm-breadcrumb>
        `);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-organization-list': FmOrganizationList;
    }
}
